import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { exchangeLinkToken, getCaptureState, isOtpChallenge, startCapture, type ScriptShot } from '@/api/capture';
import { getBearer } from '@/api/client';
import { SvApiError } from '@/api/errors';
import { createCaptureSession, currentSession, type CaptureSession } from '@/capture/captureSession';
import { useT } from '@/i18n/runtime';
import { beginVisitGeo, isCurrentFix, visitGateOpen } from '@/capture/shotGeo';
import { GeoWatchProvider } from '@/capture/GeoWatchContext';
import { useGeoWatch } from '@/capture/useGeoWatch';
import { useLatestFix } from '@/capture/useLatestFix';
import { CaptureCameraProvider, useFlowCamera } from '@/media/CaptureCameraContext';
import { repostFingerprintAfterFirstShot } from '@/media/useCaptureIntegrity';
import { useGeoEvidence } from '@/media/useGeoEvidence';
import { type RefusedShot, useCaptureStore } from '@/store/captureStore';
import { activeShots, completedIds, firstRemainingIndex, isInteriorPrompt } from '@/store/selectors';
import { tenantFrom, tenantFromSummary } from '@/theme/tenant';
import { openQueueStorage, sweepStaleVisits } from '@/upload/chunkQueue';
import { Button } from './components/Button';
import { CentredNotice, Shell } from './components/Shell';
import { BystanderInterstitial } from './BystanderInterstitial';
import { LocationGate, locationGateStatus } from './LocationGate';
import { ReviewSubmit } from './ReviewSubmit';
import { ShotClip } from './ShotClip';
import { ShotStill } from './ShotStill';

type Boot = { kind: 'loading' } | { kind: 'ready' } | { kind: 'no-link' } | { kind: 'error' };

/**
 * `/capture` — the guided capture core (SV-2-2..2-4).
 *
 * Boot: (re-)exchange if the in-memory bearer is gone (a reload) → `POST /start` (idempotent) →
 * `GET /state` (what the server already holds; recapture-only prompts) → open the IndexedDB queue
 * → create the capture session (uploader running in the background from here on) → geo row.
 *
 * Flow: the active shots (whole script, or only the recapture-requested prompts) in ordinal
 * order; a shot is "done" for navigation once it is recorded and queued on this device; a shot
 * the server REFUSED (422 SV_CHAIN_MISMATCH) drops out of "done" and the flow returns to it. The
 * bystander interstitial precedes the first interior shot. When nothing remains → review/submit.
 */
export function CaptureFlow(): JSX.Element {
  const { t } = useT();
  const navigate = useNavigate();
  const store = useCaptureStore;
  const linkToken = useCaptureStore((s) => s.linkToken);
  const summary = useCaptureStore((s) => s.summary);
  const visit = useCaptureStore((s) => s.visit);
  const captureState = useCaptureStore((s) => s.captureState);
  const completed = useCaptureStore((s) => s.completed);
  const sealedLocal = useCaptureStore((s) => s.sealedLocal);
  const refused = useCaptureStore((s) => s.refused);
  const bystanderAcknowledged = useCaptureStore((s) => s.bystanderAcknowledged);
  const skipped = useCaptureStore((s) => s.skipped);
  const [boot, setBoot] = useState<Boot>({ kind: 'loading' });
  const [retry, setRetry] = useState(0);
  const sessionRef = useRef<CaptureSession | null>(null);
  /** F-23: per shot, the refusal last seen and how many have arrived — the shot screen's key (see below). */
  const takes = useRef(new Map<string, { seen: RefusedShot; n: number }>());

  useEffect(() => {
    let active = true;
    (async () => {
      setBoot({ kind: 'loading' });
      const token = store.getState().linkToken;
      if (!token) {
        setBoot({ kind: 'no-link' });
        return;
      }
      try {
        if (!getBearer() || !store.getState().summary) {
          const ex = await exchangeLinkToken(token);
          if (!active) return;
          if (isOtpChallenge(ex)) {
            // An OTP-fronted link re-opened on a reload: the code goes to the representative's email/phone and only
            // the front door can take it. Go there; the queue keeps this visit's chunks for the resume.
            navigate(`/v/${encodeURIComponent(token)}`, { replace: true });
            return;
          }
          store.getState().setExchange(ex.summary, ex.expiresAt);
        }
        const [start, state] = await Promise.all([startCapture(), getCaptureState()]);
        if (!active) return;
        store.getState().setVisit(start);
        store.getState().setCaptureState(state);
        let session = currentSession();
        if (!session || session.visitId !== start.visitId) {
          session?.dispose();
          const { storage, persistence } = await openQueueStorage();
          if (!active) return;
          session = await createCaptureSession({ visitId: start.visitId, nonce: start.nonce, storage, persistence });
          store.getState().setPersistence(persistence);
        }
        sessionRef.current = session;
        // DEC-097 T2 — boot sweep. Whatever else this phone is carrying from an earlier visit goes
        // now: the receipt purge cannot run for a visit that was never submitted, or whose tab was
        // closed on the receipt. Deliberately NOT awaited — deleting a stale visit's ten clips is
        // real work on a cheap handset, and the customer opened this link to capture, not to wait
        // for housekeeping; it touches no row of the current visit, so nothing here races with the
        // queue. It never throws (see sweepStaleVisits, which also documents the one visit class it
        // leaves alone: bytes the server has not got yet).
        void sweepStaleVisits(session.storage, start.visitId);
        setBoot({ kind: 'ready' });
      } catch (e) {
        if (!active) return;
        setBoot(e instanceof SvApiError && e.status === 401 ? { kind: 'no-link' } : { kind: 'error' });
      }
    })();
    return () => {
      active = false;
    };
  }, [retry, store, navigate]);

  const tenant = useMemo(() => tenantFromSummary(summary) ?? tenantFrom(''), [summary]);
  const active = useMemo(() => activeShots(visit?.script ?? [], captureState), [visit, captureState]);
  const doneForNav = useMemo(() => completedIds(captureState, [...completed, ...sealedLocal, ...skipped]), [captureState, completed, sealedLocal, skipped]);
  const index = firstRemainingIndex(active, doneForNav);
  const shot: ScriptShot | undefined = active[index];
  const needsBystander = !!shot && isInteriorPrompt(shot.promptKey) && !bystanderAcknowledged;
  const firstInteriorDone = active.some((s, i) => i < index && isInteriorPrompt(s.promptKey));

  // US-03482 iPhone black-video RCA (2026-11-03): the capture owns ONE position watch (G2) and ONE camera session (G1),
  // shared by every step through context — a step change never re-opens either (Chrome on iOS re-prompts on every new
  // acquisition, and a prompt landing while the camera starts could leave WebKit with a frameless track). Both run
  // while a shot (or the gate before it) is on screen, and stop at review or when the capture is left.
  const capturing = boot.kind === 'ready' && !!shot;
  // A reload that boots straight into review still owes the visit its G1 row and permission: the watch runs there
  // too until the report has its first answer (one background read), then stops.
  const [geoAnswered, setGeoAnswered] = useState(false);
  const watch = useGeoWatch(capturing || (boot.kind === 'ready' && !geoAnswered));
  // The visit-level geo row (G1 leg), written from that same watch — no request of its own.
  const geo = useGeoEvidence(boot.kind === 'ready', watch);
  useEffect(() => {
    if (geo.permission !== 'PENDING') setGeoAnswered(true);
  }, [geo.permission]);
  useEffect(() => {
    if (geo.geoRowId) store.getState().setGeoRecorded(true);
  }, [geo.geoRowId, store]);
  // US-03482 P1-3: the fix the gate waits for — the SAME one the chunk header binds (shotGeo.latestFix()), and only one
  // of THIS visit: a fix held from an earlier visit in this tab is dropped when the visit changes, and a fix older than
  // the visit (less the 30 s maximumAge) does not open the gate.
  if (visit) beginVisitGeo(visit.visitId);
  const held = useLatestFix();
  const fix = isCurrentFix(held) ? held : null;
  // A re-try may have left the shared watch at LOW accuracy (the network position); once the gate opens on a fix, put
  // it back to high, so every shot's stamp and header get the GPS.
  const { restart: restartWatch, highAccuracy: watchHigh } = watch;
  const gateOpen = fix !== null;
  useEffect(() => {
    if (gateOpen && !watchHigh) restartWatch({ highAccuracy: true });
  }, [gateOpen, watchHigh, restartWatch]);
  // The capture fingerprint must land on a shot the be keeps (it drops a post made before any shot has a chunk — and
  // with one camera session the only collection is at step 1): re-post the cached one after the first acknowledged shot.
  // Keyed on the COUNT of acknowledged shots: a failed re-post (refunded) is tried again by the next one.
  const doneCount = completed.length;
  const permsRef = useRef<Readonly<Record<string, string | boolean | null>>>({});
  useEffect(() => {
    if (doneCount === 0) return;
    void repostFingerprintAfterFirstShot(permsRef.current);
  }, [doneCount]);
  // G1: the microphone is asked for once, up front, when the script has any clip.
  const sessionAudio = (visit?.script ?? []).some((s) => s.kind === 'CLIP');
  const camera = useFlowCamera(capturing, sessionAudio);
  // The full permission set the re-post carries — the same keys the shot screens' collection posts.
  permsRef.current = {
    camera: camera.phase === 'live',
    microphone: camera.hasAudio,
    geolocation: geo.permission,
    motion: sessionRef.current?.imu.permission ?? null,
  };

  if (boot.kind === 'no-link' || !linkToken) {
    return <Shell tenant={null}><CentredNotice title={t('sv.entry.linkRequired.title')} body={t('sv.entry.linkRequired.body')} /></Shell>;
  }
  if (boot.kind === 'error') {
    return (
      <Shell tenant={tenant}>
        <CentredNotice
          title={t('sv.capture.startFail.title')}
          body={t('sv.capture.startFail.body')}
          action={<Button onClick={() => setRetry((n) => n + 1)}>{t('sv.action.tryAgain')}</Button>}
        />
      </Shell>
    );
  }
  if (boot.kind === 'loading' || !visit || !sessionRef.current) {
    return (
      <Shell tenant={tenant}>
        <div className="app-view center"><div className="app-preparing" role="status">{t('sv.capture.preparing')}</div></div>
      </Shell>
    );
  }

  const session = sessionRef.current;

  if (!shot) {
    return (
      <Shell tenant={tenant}>
        <ReviewSubmit
          tenant={tenant}
          session={session}
          active={active}
          geoPermission={geo.permission}
          onSubmitted={() => navigate('/done', { replace: true })}
        />
      </Shell>
    );
  }

  // US-03482 P1-3 (decided 2026-11-02): no shot starts without a position — the gate shows the way to switch the
  // phone's Location on and re-tries on its own. A browser with no Geolocation API at all is let through: the honest
  // absence is posted (UNSUPPORTED) and the server's geo checks cannot pass a case without a device fix. Once open for
  // this visit it stays open (`visitGateOpen`): a rough reading later never takes the camera screen away mid-shot.
  const gate = visitGateOpen(held) ? 'open' : locationGateStatus(fix, geo.phase, geo.permission);
  if (gate !== 'open' && gate !== 'unsupported') {
    return (
      <Shell tenant={tenant}>
        <LocationGate phase={geo.phase} permission={geo.permission} watch={watch} tenantName={tenant.name} />
      </Shell>
    );
  }

  if (needsBystander && !firstInteriorDone) {
    return <Shell tenant={tenant}><BystanderInterstitial tenant={tenant} body={summary?.notices?.find((n) => n.role === 'BYSTANDER_PROMPT')?.body ?? null} onAck={() => store.getState().setBystanderAcknowledged()} /></Shell>;
  }

  const refusal = refused[shot.shotId] ?? null;
  // F-23: a refusal ARRIVING gives the shot a fresh screen (a new take, the reason on it). Clearing it does not: beginShot
  // clears it on "Use this" while that accept is still running in this very screen, and a remount there restarted the
  // camera under it. So the key counts refusals; it never goes back when one is cleared.
  const seen = takes.current.get(shot.shotId);
  if (refusal && refusal !== seen?.seen) takes.current.set(shot.shotId, { seen: refusal, n: (seen?.n ?? 0) + 1 });
  const take = takes.current.get(shot.shotId)?.n ?? 0;
  const common = {
    tenant,
    session,
    shot,
    stepIndex: index,
    stepTotal: active.length,
    refusal,
    recaptureOnly: !!captureState?.recaptureOnly?.length,
    geoPermission: geo.permission,
  };
  return (
    <Shell tenant={tenant} camera>
      <CaptureCameraProvider camera={camera}>
        <GeoWatchProvider watch={watch}>
          {shot.kind === 'CLIP' ? <ShotClip key={`${shot.shotId}-${take}`} {...common} /> : <ShotStill key={`${shot.shotId}-${take}`} {...common} />}
        </GeoWatchProvider>
      </CaptureCameraProvider>
    </Shell>
  );
}
